import styled from 'styled-components';
import tw, { css } from 'twin.macro';
import Checkbox from '../../../components/Checkbox/Checkbox';
import { Caption } from '../../../styled components/Typography';
import { theme } from '../../../../tailwind.config';

/* The frame of the list view's toolbar: two stacked bands, each with one job.
 * The scope band says which bookings are included (the period and the
 * filters), the display band how they are shown and how fresh they are. They
 * share one frame and are told apart by their tint, so the whole toolbar reads
 * as one block rather than as a row of loose controls. */

export const ToolbarFrame = styled.div(tw`mb-16`);

/* The band carries no horizontal padding of its own: its rows do, so a divider
   between two of them runs the full width of the frame rather than stopping
   short of it */
export const ToolbarBand = styled.div<{ $tone: 'scope' | 'display' }>(({ $tone }) => [
  tw`border border-solid border-silver-lighten-15`,
  $tone === 'scope' && tw`bg-silver-lighten-30 rounded-t`,
  /* One step darker than the scope band, and hung directly under it: the two
     borders would otherwise double up into a two-pixel line */
  $tone === 'display' && tw`bg-silver-lighten-25 border-t-0 rounded-b`
]);

/* Every row keeps the same height whatever it holds — a row of filter chips
   included, which is why the chips scroll sideways instead of wrapping. The
   toolbar then never changes height under the user, so nothing below it moves
   when a filter is added or removed. 48px leaves the 28px controls a row they
   sit in rather than fill */
export const ToolbarRow = styled.div(() => [
  /* items-stretch rather than items-center: it is what lets the divider
     between a row's label and its content run the row's full height */
  tw`flex items-stretch h-48 px-8 md:px-12`,
  /* Two rows of one band are told apart by a hairline in the frame's own
     colour — the period and the filters are two lines of one group, not two
     bands */
  css`
    & + & {
      ${tw`border-0 border-t border-solid border-silver-lighten-15`}
    }
  `
]);

/* The display band's row. It is one line from md up, and below that it wraps:
   the view mode and the refresh line share the first line, and the two
   switches take a line each underneath, where their labels have the width to
   wrap rather than being cut short. It is the one row that grows, and it grows
   with its content rather than with the number of filters */
export const DisplayRow = styled.div(
  tw`flex flex-wrap items-center px-8 md:px-12 py-4 md:py-0 md:h-48`
);

/* A switch of the display band. Below md it has a line of its own, where its
   label is set whole; from md up it lines up beside the view mode. Both orders
   are given: the refresh line sits between the switches and the view mode
   below md, and after them from md up */
export const DisplaySwitchSlot = styled.div<{ $order: number; $orderFromMd: number }>(
  ({ $order, $orderFromMd }) => [
    tw`w-full min-w-0 mt-4 md:(w-auto mt-0 ml-12)`,
    css`
      order: ${$order};

      @media (min-width: ${theme.screens.md}) {
        order: ${$orderFromMd};
      }
    `
  ]
);

/* The micro-label of a scope row. Both rows share this gutter: it is what
   makes the period and the filters read as two lines of one group */
export const ToolbarLabel = styled(Caption).attrs(() => ({
  $transform: 'uppercase',
  $color: 'gray light'
}))`
  ${tw`flex-none self-center truncate font-medium tracking-wide`}
  /* A gutter of 3.5rem (56px) on a phone, where a third of the width cannot go
     to a label, and 4.5rem from md up, which holds the longest of them
     comfortably in any language. Neither is on the width scale, which stops at
     48px. A label too long for its gutter — a translation of 'FILTERS', say —
     is cut short rather than pushing the divider out of line */
  width: 3.5rem;

  @media (min-width: ${theme.screens.md}) {
    width: 4.5rem;
  }
`;

/* What the label labels, divided off from it by a hairline in the frame's own
   colour. min-w-0 lets an overflowing control (the filter chips) scroll inside
   the row instead of stretching it */
export const ToolbarRowContent = styled.div(
  tw`flex items-center flex-1 min-w-0 pl-8 md:pl-12 border-0 border-l border-solid border-silver-lighten-15`
);

/* A panel hung from a control in the toolbar: the date range picker, the
   filter builder, the refresh menu. It hangs from the bottom edge of its
   control and, unless it is at the right edge of the band, lines up with that
   control's left edge — a panel wider than its button would otherwise run off
   the side of the toolbar */
export const ToolbarDropdown = styled.div<{ $align: 'left' | 'right' }>(
  ({ $align }) => [
    tw`absolute mt-4 z-100`,
    /* The inset scale has no 'full' */
    `top: 100%;`,
    $align === 'left' ? tw`left-0` : tw`right-0`
  ]
);

/* A switch of the toolbar's display band — the sets toggle, the past bookings
 * toggle — as that band needs it: reading order label first and the switch
 * after it, the two lined up on one centre line, and a label in the size of
 * the controls it sits between rather than in body type.
 *
 * The switch draws its track and knob as the label's pseudo-elements, pinned
 * to the label's left; putting the switch after the label is a matter of
 * moving those to its right edge — and of dropping the vertical padding the
 * component adds, which is what pushes the label text below the track. */
export const ToolbarSwitch = styled(Checkbox)`
  /* Only as wide as it needs to be, so the switch sits directly after the
     label rather than at the far side of the line the two share. From md it
     fills the width it is given instead, which is what lets the label be cut
     short there */
  ${tw`inline-block align-middle max-w-full min-w-0 py-0`}

  @media (min-width: ${theme.screens.md}) {
    ${tw`block w-full`}
  }

  & label {
    ${tw`flex items-center min-w-0 pl-0`}
    min-height: 1.75rem;
    /* The room the track needs, now at the other end */
    padding-right: 3.5rem;
  }

  /* Below md the label has a line of its own to wrap onto, so it is set whole;
     from md up it shares a row and is cut short where that row runs out */
  & label span {
    ${tw`text-sm leading-18 block pl-0 pr-8`}
  }

  @media (min-width: ${theme.screens.md}) {
    & label span {
      ${tw`truncate`}
    }
  }

  /* The track, and the input that is the invisible control over it. Both are
     centred on the label rather than pinned to its top, so a label of two
     lines still has its switch beside its middle */
  & input[type='checkbox'],
  & label:before {
    ${tw`top-1/2 left-auto right-0 transform -translate-y-1/2`}
  }

  /* The knob, mirrored: at the track's left end when off, at its right when on */
  & label:after,
  & input:checked ~ label:after {
    ${tw`top-1/2 transform -translate-y-1/2`}
  }

  & label:after {
    ${tw`left-auto`}
    right: 1.9rem;
    transition: background 0.3s ease, right 0.3s ease;
  }

  & input:checked ~ label:after {
    ${tw`left-auto`}
    /* Not on the inset scale (0/auto/1/2 only) - matches the knob offset
       Checkbox.style.js itself uses */
    right: 0.125rem;
  }
`;
